import { useEffect, useState } from 'react'
import { Modal } from 'medere-ui-framework'
import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { RadioButton } from 'primereact/radiobutton'
import { ContractHonorariumOptionDTO } from '../../../dto/contractHonorarium/ContractHonorariumDTO'
import { EDistributionContractType } from '../../../enums/EDistributionContractType'
import { isFixedAmount } from '../contractHonorariumFormat'
import PercentageInput from './PercentageInput'

export interface BulkEditValues {
  distributionTypeId: number
  honoPercentage: number | null
  copaymentPercentage: number | null
  diferentialPercentage: number | null
  fixedAmount: number | null
  fromDate: Date | null
}

type Mode = 'correct' | 'newFrom'
type Field = 'distributionTypeId' | 'honoPercentage' | 'copaymentPercentage' | 'diferentialPercentage' | 'fixedAmount' | 'fromDate'
type Errors = Partial<Record<Field, string>>

interface BulkEditContractHonorariumModalProps {
  visible: boolean
  count: number
  distributionTypes: ContractHonorariumOptionDTO[]
  confirming: boolean
  onCancel: () => void
  onConfirm: (values: BulkEditValues) => void
}

const PERCENTAGE_FIELDS: { field: 'honoPercentage' | 'copaymentPercentage' | 'diferentialPercentage'; label: string; required: string }[] = [
  { field: 'honoPercentage', label: 'Porcentaje de honorarios', required: 'Completá el porcentaje de honorarios' },
  { field: 'copaymentPercentage', label: 'Porcentaje de coseguro', required: 'Completá el porcentaje de coseguro' },
  { field: 'diferentialPercentage', label: 'Porcentaje de diferencial', required: 'Completá el porcentaje de diferencial' },
]

const MODE_HELP: Record<Mode, string> = {
  correct: 'Los valores se reemplazan desde el inicio de cada distribución, también para lo pendiente de liquidar. El valor anterior queda en el historial.',
  newFrom: 'Cada distribución se cierra el día anterior a la fecha y se crea una nueva desde esa fecha con los nuevos valores.',
}

function tomorrow(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
}

interface State {
  distributionTypeId: number | null
  honoPercentage: number | null
  copaymentPercentage: number | null
  diferentialPercentage: number | null
  fixedAmount: number | null
  mode: Mode
  fromDate: Date | null
}

function initialState(): State {
  return {
    distributionTypeId: EDistributionContractType.PERCENTAGE,
    honoPercentage: null,
    copaymentPercentage: null,
    diferentialPercentage: null,
    fixedAmount: null,
    mode: 'correct',
    fromDate: tomorrow(),
  }
}

function validate(state: State): Errors {
  const errors: Errors = {}
  if (state.distributionTypeId == null) errors.distributionTypeId = 'Elegí el tipo de distribución'
  if (isFixedAmount(state.distributionTypeId)) {
    if (state.fixedAmount == null) errors.fixedAmount = 'Completá el importe fijo'
    else if (state.fixedAmount < 0) errors.fixedAmount = 'Ingresá un importe igual o mayor a 0'
  } else {
    for (const { field, required } of PERCENTAGE_FIELDS) {
      const value = state[field]
      if (value == null) errors[field] = required
      else if (value < 0 || value > 100) errors[field] = 'Ingresá un porcentaje entre 0 y 100'
    }
  }
  if (state.mode === 'newFrom') {
    if (!state.fromDate) errors.fromDate = 'Completá la fecha desde'
    else if (state.fromDate < tomorrow()) errors.fromDate = 'Ingresá una fecha desde posterior a hoy'
  }
  return errors
}

function FieldError({ id, message }: Readonly<{ id: string; message?: string }>) {
  if (!message) return null
  return (
    <small id={id} className='chd-field-error'>
      <i className='pi pi-exclamation-triangle' aria-hidden='true' />
      {message}
    </small>
  )
}

export default function BulkEditContractHonorariumModal({
  visible,
  count,
  distributionTypes,
  confirming,
  onCancel,
  onConfirm,
}: Readonly<BulkEditContractHonorariumModalProps>) {
  const [state, setState] = useState<State>(initialState)
  const [errors, setErrors] = useState<Errors>({})

  useEffect(() => {
    if (!visible) return
    setState(initialState())
    setErrors({})
  }, [visible])

  function update<K extends keyof State>(field: K, value: State[K]) {
    const next = { ...state, [field]: value }
    setState(next)
    if (Object.keys(errors).length > 0) setErrors(validate(next))
  }

  function confirm() {
    const found = validate(state)
    setErrors(found)
    if (Object.keys(found).length > 0 || state.distributionTypeId == null) return
    const fixed = isFixedAmount(state.distributionTypeId)
    onConfirm({
      distributionTypeId: state.distributionTypeId,
      honoPercentage: fixed ? null : state.honoPercentage,
      copaymentPercentage: fixed ? null : state.copaymentPercentage,
      diferentialPercentage: fixed ? null : state.diferentialPercentage,
      fixedAmount: fixed ? state.fixedAmount : null,
      fromDate: state.mode === 'newFrom' ? state.fromDate : null,
    })
  }

  function a11y(field: Field, errorId: string) {
    return {
      'aria-invalid': errors[field] ? true : undefined,
      'aria-describedby': errors[field] ? errorId : undefined,
    }
  }

  const fixedAmountSelected = isFixedAmount(state.distributionTypeId)

  return (
    <Modal
      visible={visible}
      setVisible={(nextVisible) => {
        if (!nextVisible) onCancel()
      }}
      header={<span className='chd-modal-title'>Edición en bloque</span>}
      width='w-large'
      className='chd-modal'
      footerButtonLeftText='Cancelar'
      onClickLeftBtn={onCancel}
      footerButtonRightText='Guardar'
      onClickRightBtn={confirm}
      rightButtonDisabled={confirming}
    >
      <div className='chd-modal-body chd-form'>
        <p className='chd-confirm-text'>
           Contratos seleccionados: <strong>{count}</strong>
        </p>

        <div className='chd-field'>
          <span id='chd-bulk-distribution-type-label' className='chd-field-label'>
            Tipo de distribución <span className='md-req'>*</span>
          </span>
          <div className='chd-radio-group' role='radiogroup' aria-labelledby='chd-bulk-distribution-type-label'>
            {distributionTypes.map((type) => (
              <div key={type.id} className='chd-radio-option'>
                <RadioButton
                  inputId={`chd-bulk-distribution-type-${type.id}`}
                  name='bulkDistributionType'
                  value={type.id}
                  checked={state.distributionTypeId === type.id}
                  onChange={(event) => update('distributionTypeId', typeof event.value === 'number' ? event.value : null)}
                />
                <label htmlFor={`chd-bulk-distribution-type-${type.id}`}>{type.name}</label>
              </div>
            ))}
          </div>
          <FieldError id='chd-bulk-distribution-type-error' message={errors.distributionTypeId} />
        </div>

        {fixedAmountSelected ? (
          <div className='chd-row chd-row-fit'>
            <div className='chd-field'>
              <label htmlFor='chd-bulk-fixed-amount'>
                Importe fijo <span className='md-req'>*</span>
              </label>
              <InputNumber
                inputId='chd-bulk-fixed-amount'
                value={state.fixedAmount}
                mode='decimal'
                locale='es-AR'
                minFractionDigits={2}
                maxFractionDigits={2}
                min={0}
                prefix='$ '
                placeholder='$ 0,00'
                className={errors.fixedAmount ? 'p-invalid' : ''}
                onValueChange={(event) => update('fixedAmount', typeof event.value === 'number' ? event.value : null)}
                {...a11y('fixedAmount', 'chd-bulk-fixed-amount-error')}
              />
              <FieldError id='chd-bulk-fixed-amount-error' message={errors.fixedAmount} />
            </div>
          </div>
        ) : (
          <div className='chd-row chd-row-fit'>
            {PERCENTAGE_FIELDS.map(({ field, label }) => (
              <div key={field} className='chd-field'>
                <label htmlFor={`chd-bulk-${field}`}>
                  {label} <span className='md-req'>*</span>
                </label>
                <PercentageInput
                  inputId={`chd-bulk-${field}`}
                  value={state[field]}
                  invalid={Boolean(errors[field])}
                  onChange={(value) => update(field, value)}
                  {...a11y(field, `chd-bulk-${field}-error`)}
                />
                <FieldError id={`chd-bulk-${field}-error`} message={errors[field]} />
              </div>
            ))}
          </div>
        )}

        <div className='chd-field'>
          <span id='chd-bulk-mode-label' className='chd-field-label'>Cómo aplicar los valores</span>
          <div className='chd-radio-group' role='radiogroup' aria-labelledby='chd-bulk-mode-label'>
            <div className='chd-radio-option'>
              <RadioButton
                inputId='chd-bulk-mode-correct'
                name='bulkMode'
                value='correct'
                checked={state.mode === 'correct'}
                onChange={() => update('mode', 'correct')}
              />
              <label htmlFor='chd-bulk-mode-correct'>Corregir valores (mantiene las fechas)</label>
            </div>
            <div className='chd-radio-option'>
              <RadioButton
                inputId='chd-bulk-mode-new-from'
                name='bulkMode'
                value='newFrom'
                checked={state.mode === 'newFrom'}
                onChange={() => update('mode', 'newFrom')}
              />
              <label htmlFor='chd-bulk-mode-new-from'>Nuevos valores desde una fecha</label>
            </div>
          </div>
          <span className='chd-hint chd-hint--two-lines'>
            <i className='pi pi-info-circle' aria-hidden='true' />
            {MODE_HELP[state.mode]}
          </span>
        </div>

        <div className={`chd-row chd-row-fit${state.mode === 'newFrom' ? '' : ' chd-keep-space-hidden'}`}>
          <div className='chd-field'>
            <label htmlFor='chd-bulk-from-date'>
              Vigente desde <span className='md-req'>*</span>
            </label>
            <Calendar
              inputId='chd-bulk-from-date'
              value={state.fromDate}
              dateFormat='dd/mm/yy'
              showIcon
              minDate={tomorrow()}
              placeholder='dd/mm/aaaa'
              appendTo={document.body}
              className={errors.fromDate ? 'p-invalid' : ''}
              onChange={(event) => update('fromDate', event.value instanceof Date ? event.value : null)}
              aria-invalid={errors.fromDate ? true : undefined}
              aria-describedby={errors.fromDate ? 'chd-bulk-from-date-error' : undefined}
            />
            <FieldError id='chd-bulk-from-date-error' message={errors.fromDate} />
          </div>
        </div>
      </div>
    </Modal>
  )
}
